import React, { useState, useEffect } from "react";
import styled from "@emotion/styled";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useQuery } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import { useUser } from "../../../contexts/UserContext";
import { useSpace } from "../../../contexts/SpaceContext";
import { GET_DASHBOARD_STATS } from "./graphqlQueries";

import {
  Grid,
  Divider as MuiDivider,
  Typography as MuiTypography,
  Card as MuiCard,
  CardContent,
  Box,
  Chip,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
} from "@mui/material";
import { spacing } from "@mui/system";
import { green, red, orange, blue } from "@mui/material/colors";
import {
  CalendarToday as CalendarIcon,
  Event as EventIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Schedule as ScheduleIcon,
  TrendingUp as TrendingUpIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";

// Chart components (we'll keep the existing chart components for now)
import Actions from "./Actions";
import BarChart from "./BarChart";
import LineChart from "./LineChart";
import DoughnutChart from "./DoughnutChart";

const Divider = styled(MuiDivider)(spacing);
const Typography = styled(MuiTypography)(spacing);
const Card = styled(MuiCard)(spacing);

// Custom styled components for statistics cards
const StatsCard = styled(Card)`
  height: 100%;
  background: linear-gradient(135deg, ${(props) => props.gradient});
  color: white;
  cursor: ${(props) => (props.clickable ? "pointer" : "default")};
  transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;

  &:hover {
    ${(props) =>
      props.clickable &&
      `
      transform: translateY(-4px);
      box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
    `}
  }

  .MuiCardContent-root {
    position: relative;
    &:last-child {
      padding-bottom: 16px;
    }
  }
`;

const StatsIcon = styled(Box)`
  position: absolute;
  top: 16px;
  right: 16px;
  opacity: 0.3;
`;

const StatsNumber = styled(Typography)`
  font-size: 2.5rem;
  font-weight: 700;
  margin: 8px 0;
`;

const StatsChange = styled(Chip)`
  margin-top: 8px;
`;

// Clickable secondary card style
const ClickableCard = styled(Card)`
  height: 100%;
  cursor: pointer;
  transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  }
`;

function StatCard({
  title,
  value,
  icon: Icon,
  gradient,
  change,
  changeType = "positive",
  onClick,
}) {
  return (
    <StatsCard gradient={gradient} onClick={onClick} clickable={!!onClick}>
      <CardContent>
        <StatsIcon>
          <Icon sx={{ fontSize: 48 }} />
        </StatsIcon>
        <Typography variant="h6" component="h3" gutterBottom>
          {title}
        </Typography>
        <StatsNumber variant="h3" component="div">
          {value}
        </StatsNumber>
        {change && (
          <StatsChange
            label={change}
            size="small"
            sx={{
              backgroundColor:
                changeType === "positive"
                  ? "rgba(76, 175, 80, 0.2)"
                  : changeType === "info"
                  ? "rgba(33, 150, 243, 0.2)"
                  : "rgba(244, 67, 54, 0.2)",
              color:
                changeType === "positive"
                  ? "#4CAF50"
                  : changeType === "info"
                  ? "#2196F3"
                  : "#f44336",
              fontWeight: 600,
            }}
          />
        )}
      </CardContent>
    </StatsCard>
  );
}

function RecentBookingsTable({ bookings }) {
  const getStatusColor = (status) => {
    switch (status) {
      case "confirmed":
        return green[500];
      case "pending":
        return orange[500];
      case "canceled":
        return red[500];
      case "rejected":
        return red[700];
      default:
        return "default";
    }
  };

  const getStatusLabel = (status) => {
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h5" gutterBottom>
          Recent Bookings
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Event</TableCell>
                <TableCell>Date & Time</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {bookings.map((booking) => (
                <TableRow key={booking.id} hover>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {booking.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {booking.email}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2">
                        {booking.eventType?.title || "Event"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {booking.eventType?.duration || "30"} minutes
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {new Date(booking.date).toLocaleDateString()}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {booking.timeStart} - {booking.timeEnd}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getStatusLabel(booking.status)}
                      size="small"
                      sx={{
                        backgroundColor: getStatusColor(booking.status),
                        color: "white",
                        fontWeight: 600,
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Button
                      startIcon={<VisibilityIcon />}
                      size="small"
                      variant="outlined"
                    >
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {bookings.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography color="text.secondary">
                      No recent bookings found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
}

function EventTypeOverview({ eventTypeStats }) {
  return (
    <Card>
      <CardContent>
        <Typography variant="h5" gutterBottom>
          Event Type Performance
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Event Type</TableCell>
                <TableCell align="right">Total Bookings</TableCell>
                <TableCell align="right">Completed</TableCell>
                <TableCell align="right">Canceled</TableCell>
                <TableCell align="right">Success Rate</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {eventTypeStats.map((stat) => {
                const successRate =
                  stat.totalBookings > 0
                    ? (
                        (stat.completedBookings / stat.totalBookings) *
                        100
                      ).toFixed(1)
                    : 0;

                return (
                  <TableRow key={stat.eventType.id} hover>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          {stat.eventType.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {stat.eventType.duration} minutes
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body1" fontWeight={600}>
                        {stat.totalBookings}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body1" color={green[600]}>
                        {stat.completedBookings}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body1" color={red[600]}>
                        {stat.canceledBookings}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Chip
                        label={`${successRate}%`}
                        size="small"
                        sx={{
                          backgroundColor:
                            successRate >= 70
                              ? green[100]
                              : successRate >= 50
                              ? orange[100]
                              : red[100],
                          color:
                            successRate >= 70
                              ? green[700]
                              : successRate >= 50
                              ? orange[700]
                              : red[700],
                        }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
              {eventTypeStats.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography color="text.secondary">
                      No event types found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
}

function RealDashboard() {
  const { t } = useTranslation();
  const { user } = useUser();
  const { currentSpace } = useSpace();
  const navigate = useNavigate();

  // Navigation handlers for dashboard cards
  const navigateToEventTypes = () => {
    const spaceParam = currentSpace?.id ? `?spaceId=${currentSpace.id}` : "";
    navigate(`/calendar/event-types${spaceParam}`);
  };

  const navigateToBookings = (filter = "") => {
    const spaceParam = currentSpace?.id ? `?spaceId=${currentSpace.id}` : "";
    const filterParam = filter ? `&status=${filter}` : "";
    navigate(`/calendar/bookings${spaceParam}${filterParam}`);
  };

  const { loading, error, data, refetch } = useQuery(GET_DASHBOARD_STATS, {
    variables: {
      spaceId: currentSpace?.id,
    },
    errorPolicy: "all",
    fetchPolicy: "cache-and-network",
  });

  // Auto-refresh every 5 minutes
  useEffect(() => {
    const interval = setInterval(() => {
      refetch();
    }, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [refetch]);

  if (loading && !data) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error && !data) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Failed to load dashboard data: {error.message}
      </Alert>
    );
  }

  const stats = data?.getDashboardStats;

  return (
    <React.Fragment>
      <Helmet title="Calendar Dashboard" />

      <Grid justifyContent="space-between" container spacing={6}>
        <Grid item>
          <Typography variant="h3" gutterBottom>
            Calendar Dashboard
          </Typography>
          <Typography variant="subtitle1">
            {t("Welcome back")}, {user?.name || "User"}! Here's your calendar
            overview.{" "}
            <span role="img" aria-label="Calendar">
              📅
            </span>
          </Typography>
          {stats && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Status: {stats.availabilityStatus} • Space:{" "}
              {currentSpace?.name || "Default"}
            </Typography>
          )}
        </Grid>

        <Grid item>
          <Actions />
        </Grid>
      </Grid>

      <Divider my={6} />

      {stats && (
        <>
          {/* Main Statistics Cards */}
          <Grid container spacing={4} mb={4}>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Event Types"
                value={stats.totalEventTypes}
                icon={EventIcon}
                gradient="#667eea 0%, #764ba2 100%"
                change={`${stats.totalEventTypes} active`}
                changeType="info"
                onClick={navigateToEventTypes}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Total Bookings"
                value={stats.totalBookings}
                icon={CalendarIcon}
                gradient="#f093fb 0%, #f5576c 100%"
                change={`${stats.confirmedBookings} confirmed`}
                changeType="positive"
                onClick={() => navigateToBookings()}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Upcoming"
                value={stats.upcomingBookings}
                icon={ScheduleIcon}
                gradient="#4facfe 0%, #00f2fe 100%"
                change={`${stats.pendingBookings} pending`}
                changeType="info"
                onClick={() => navigateToBookings("pending")}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Completed"
                value={stats.completedBookings}
                icon={CheckIcon}
                gradient="#43e97b 0%, #38f9d7 100%"
                change={`${(
                  (stats.completedBookings / (stats.totalBookings || 1)) *
                  100
                ).toFixed(1)}% success`}
                changeType="positive"
                onClick={() => navigateToBookings("completed")}
              />
            </Grid>
          </Grid>

          {/* Secondary Statistics Cards */}
          <Grid container spacing={4} mb={4}>
            <Grid item xs={12} sm={6} md={3}>
              <ClickableCard
                sx={{ backgroundColor: green[50] }}
                onClick={() => navigateToBookings("confirmed")}
              >
                <CardContent>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Box>
                      <Typography variant="h6" color={green[800]}>
                        Confirmed
                      </Typography>
                      <Typography
                        variant="h4"
                        color={green[600]}
                        fontWeight={600}
                      >
                        {stats.confirmedBookings}
                      </Typography>
                    </Box>
                    <CheckIcon sx={{ fontSize: 40, color: green[400] }} />
                  </Box>
                </CardContent>
              </ClickableCard>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <ClickableCard
                sx={{ backgroundColor: orange[50] }}
                onClick={() => navigateToBookings("pending")}
              >
                <CardContent>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Box>
                      <Typography variant="h6" color={orange[800]}>
                        Pending
                      </Typography>
                      <Typography
                        variant="h4"
                        color={orange[600]}
                        fontWeight={600}
                      >
                        {stats.pendingBookings}
                      </Typography>
                    </Box>
                    <ScheduleIcon sx={{ fontSize: 40, color: orange[400] }} />
                  </Box>
                </CardContent>
              </ClickableCard>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <ClickableCard
                sx={{ backgroundColor: red[50] }}
                onClick={() => navigateToBookings("canceled")}
              >
                <CardContent>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Box>
                      <Typography variant="h6" color={red[800]}>
                        Canceled
                      </Typography>
                      <Typography
                        variant="h4"
                        color={red[600]}
                        fontWeight={600}
                      >
                        {stats.canceledBookings}
                      </Typography>
                    </Box>
                    <CancelIcon sx={{ fontSize: 40, color: red[400] }} />
                  </Box>
                </CardContent>
              </ClickableCard>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <ClickableCard
                sx={{ backgroundColor: blue[50] }}
                onClick={() => navigateToBookings()}
              >
                <CardContent>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Box>
                      <Typography variant="h6" color={blue[800]}>
                        This Month
                      </Typography>
                      <Typography
                        variant="h4"
                        color={blue[600]}
                        fontWeight={600}
                      >
                        {stats.monthlyBookingStats[
                          stats.monthlyBookingStats.length - 1
                        ]?.totalBookings || 0}
                      </Typography>
                    </Box>
                    <TrendingUpIcon sx={{ fontSize: 40, color: blue[400] }} />
                  </Box>
                </CardContent>
              </ClickableCard>
            </Grid>
          </Grid>

          {/* Charts Section */}
          <Grid container spacing={6} mb={4}>
            <Grid item xs={12} lg={8}>
              <LineChart monthlyStats={stats.monthlyBookingStats} />
            </Grid>
            <Grid item xs={12} lg={4}>
              <DoughnutChart bookingStats={stats} />
            </Grid>
          </Grid>

          {/* Tables Section */}
          <Grid container spacing={6}>
            <Grid item xs={12} lg={8}>
              <RecentBookingsTable bookings={stats.recentBookings} />
            </Grid>
            <Grid item xs={12} lg={4}>
              <EventTypeOverview eventTypeStats={stats.eventTypeStats} />
            </Grid>
          </Grid>
        </>
      )}

      {!stats && !loading && (
        <Alert severity="info" sx={{ mt: 2 }}>
          No calendar data available. Create some event types to get started!
        </Alert>
      )}
    </React.Fragment>
  );
}

export default RealDashboard;
